import { createContext, useContext, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import deskpassApi from '@/api/deskpass';
import localServerApi from '@/api/localServer';

import { usePageVisibilityContext } from '@/context/PageVisibility';
import { useSessionContext } from '@/context/Session';
import { useThemeContext } from '@/context/Theme';

// import { identifyUser, initializeLogRocket } from '@/lib/logRocket';
import useEvent from '@/hooks/useEvent';
import { useMountAsync } from '@/hooks/useMount';
import useStateMergeUpdater from '@/hooks/useStateMergeUpdater';
import useWatch from '@/hooks/useWatch';

import { consumerToHOC } from '@/lib/hoc';
import { sessionTrackerReady, setMetadata } from '@/lib/openReplay';
import { getNewRoomBookingMessages } from '@/lib/reservationHelpers';
import route, { pathMatchRouteName } from '@/lib/routes';

import { useNotificationContext } from './Notification';

const Context = createContext({});

// Export a dummy function and later on mutate it
export let logout = () => null;

export const initialState = {
  // Set to true when acknowledging the user is not logged or after
  // loading the current logged user.
  ready: false,
  // Set to true while the authentication process is happening
  authenticating: false,
  // Wether or not the user is logged in
  authenticated: false,
  // This will only be used during signup step while user
  // instance is not ready in the context
  invitedUserEmail: null,
  // This will only be used during signup step while user
  // instance is not ready in the context
  temporaryTeamOrganization: {},
  // When a team invite fails due to expiration or something else
  teamInviteError: false,
  // Logged user DB instance
  user: {},
  // List of message updates tied to the
  // booking approval feature
  newRoomBookingMessages: [],
  // Flag to tell if the above is being loaded
  hourlyBookingMessageUpdatesLoading: false,
  // should show SignupLoginModal
  showSignupLoginModal: false,
  continueBookingAfterAuthSuccess: false,
};

export function UserProvider({ children, serverData: { userData } }) {
  const history = useHistory();
  const location = useLocation();
  const { visible } = usePageVisibilityContext();
  const {
    createOrReplacePersistent: createPersistentNotification,
    destroyPersistent: destroyPersistentNotification,
  } = useNotificationContext();
  const { updateCustomization } = useThemeContext();
  const {
    retrieveAuthToken,
    clearSession,
    active: sessionActive,
  } = useSessionContext();

  const [state, setState] = useState({
    ...initialState,

    ...(userData && {
      ready: true,
      authenticated: userData.authenticated,
      user: userData.user,
      newRoomBookingMessages: [
        ...userData.hourlyBookingMessageUpdates,
        ...userData.officeBookingMessageUpdates,
      ],
    }),
  });

  const pollerRef = useRef(null);

  const updateState = useStateMergeUpdater(setState);

  const { invitedUserEmail, user, temporaryTeamOrganization } = state;

  const teamUser = !!(invitedUserEmail ?? user?.isTeamUser);

  const teamOrganization = useMemo(
    () => user?.teamOrganization ?? temporaryTeamOrganization ?? {},
    [user, temporaryTeamOrganization],
  );

  const teamCustomization = useMemo(
    () => user?.teamOrganization?.customization ?? {},
    [user],
  );

  const loadRoomBookingMessageUpdates = useEvent(async () => {
    updateState({ hourlyBookingMessageUpdatesLoading: true });

    if (pollerRef.current) {
      clearTimeout(pollerRef.current);
    }

    const [hourlyBookingMessageUpdates, officeBookingMessageUpdates] =
      await Promise.all([
        deskpassApi.user.getHourlyBookingMessageUpdates(),
        deskpassApi.user.getOfficeBookingMessageUpdates(),
      ]);

    const newRoomBookingMessages = await getNewRoomBookingMessages([
      ...hourlyBookingMessageUpdates,
      ...officeBookingMessageUpdates,
    ]);

    updateState({
      newRoomBookingMessages,
      hourlyBookingMessageUpdatesLoading: false,
    });
  });

  const pollRoomMessageUpdates = useEvent(() => {
    const poller = setTimeout(() => {
      if (state.authenticated && visible) {
        loadRoomBookingMessageUpdates();
        pollRoomMessageUpdates();
      }
    }, 1000 * 60);

    pollerRef.current = poller;
  });

  const loadUser = useEvent(async () => {
    const [user] = await Promise.all([
      deskpassApi.user.getSelf(),
      pollRoomMessageUpdates(),
    ]);

    if (user.teamOrganization) {
      updateCustomization(user?.teamOrganization?.customization ?? {});
    }

    updateState({ user });

    return user;
  });

  const login = useEvent(async (email, password, dpCsrfToken) => {
    updateState({ authenticating: true });

    try {
      await localServerApi.auth.login(email, password, dpCsrfToken);
      await retrieveAuthToken();
      const user = await loadUser();

      sessionTrackerReady?.then(() => {
        setMetadata(user);
      });

      updateState({ authenticating: false, authenticated: true });
      closeSignupLoginModal();
      return user;
    } catch (err) {
      updateState({ authenticating: false, authenticated: false });
      throw err;
    }
  });

  const _logout = useEvent(async () => {
    await localServerApi.auth.logout();
    clearSession();
    // stop polling message updates
    if (pollerRef.current) {
      clearTimeout(pollerRef.current);
    }
    updateState({
      user: {},
      authenticated: false,
      ready: true,
    });
  });

  const openSignupLoginModal = useEvent(() => {
    if (!state.authenticated) updateState({ showSignupLoginModal: true });
  });

  const closeSignupLoginModal = useEvent(() => {
    updateState({ showSignupLoginModal: false });
  });

  useMountAsync(async () => {
    logout = _logout;

    if (sessionActive && visible) {
      pollRoomMessageUpdates();
    }

    if (!!userData) {
      return;
    }

    if (!sessionActive) {
      return updateState({ ready: true });
    }

    try {
      const user = await loadUser();

      sessionTrackerReady?.then(() => {
        setMetadata(user);
      });

      updateState({ authenticated: true, ready: true });
    } catch (err) {
      console.error('Error loading user', err);
      _logout();
    }
  });

  useWatch(
    (prevVisible) => {
      if (state.authenticated && !prevVisible && visible) {
        pollRoomMessageUpdates();
      }
    },
    [visible, state.authenticated],
    false, // Skip mount
  );

  const teamMemberWithIndividualPayON = teamUser && user?.useUserBilling;
  const teamMemberNeedsToSetCreditCard =
    teamMemberWithIndividualPayON && !user?.currentPaymentMethod;

  useWatch(
    (currentPaymentMethodPrevValue) => {
      if (teamMemberWithIndividualPayON && !user?.currentPaymentMethod) {
        createPersistentNotification(
          'CREDIT_CARD_INDIVIDUAL_PAY',
          "Your Team Manager has set you up for Individual Billing. You won't be able to make reservations until you enter your credit card information on your Account Info page.",
          'warning',
        );

        history.replace(route('accountShow'));
      }

      if (!currentPaymentMethodPrevValue && user?.currentPaymentMethod) {
        destroyPersistentNotification('CREDIT_CARD_INDIVIDUAL_PAY');
      }
    },
    [user?.currentPaymentMethod, teamMemberWithIndividualPayON],
  );

  useWatch(() => {
    const onSignupEmailVerificationPage = [
      'signupCheckEmail',
      'verifySignupEmail',
    ].some((routeName) => pathMatchRouteName(location.pathname, routeName));

    const showEmailConfirmationBanner =
      state.authenticated &&
      !teamUser &&
      user?.signupEmailVerified === false &&
      !onSignupEmailVerificationPage;

    if (showEmailConfirmationBanner) {
      createPersistentNotification(
        'SIGNUP_EMAIL_VERIFICATION',
        'Please confirm your email before making reservations.',
        'warning',
        { url: route('signupCheckEmail') },
      );
    } else {
      destroyPersistentNotification('SIGNUP_EMAIL_VERIFICATION');
    }
  }, [
    state.authenticated,
    teamUser,
    user?.signupEmailVerified,
    location.pathname,
  ]);

  const updateUser = useEvent(async (userPayload, noReload = false) => {
    const result = await deskpassApi.user.updateSelf(userPayload);

    if (noReload) return result;

    return await loadUser();
  });

  const loadTeamInvitation = useEvent(async (hash) => {
    try {
      const { email, firstName, lastName, teamOrganization } =
        await deskpassApi.user.getTeamInvitation(hash);

      updateState({
        invitedUserFirstName: firstName,
        invitedUserLastName: lastName,
        invitedUserEmail: email,
        temporaryTeamOrganization: teamOrganization,
      });

      return { email, firstName, lastName, teamOrganization };
    } catch (err) {
      updateState({ teamInviteError: true });
    }
  });

  /**
   * Signifies to proceed to confirm modal after successful authentication.
   * Used before and after signup/login prompt during a booking.
   */
  const setContinueBookingAfterAuthSuccess = useEvent((status) => {
    updateState({ continueBookingAfterAuthSuccess: status });
  });

  const context = useMemo(
    () => ({
      ...state,
      updateUser,
      loadUser,
      login,
      logout: _logout,
      teamUser,
      teamOrganization,
      teamCustomization,
      loadTeamInvitation,
      openSignupLoginModal,
      closeSignupLoginModal,
      setContinueBookingAfterAuthSuccess,
      teamMemberNeedsToSetCreditCard,
    }),
    [
      state,
      updateUser,
      loadUser,
      login,
      _logout,
      teamUser,
      teamOrganization,
      teamCustomization,
      loadTeamInvitation,
      openSignupLoginModal,
      closeSignupLoginModal,
      setContinueBookingAfterAuthSuccess,
      teamMemberNeedsToSetCreditCard,
    ],
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
}

export const withUserContext = consumerToHOC(Context.Consumer, 'userContext');
export const useUserContext = () => useContext(Context);
export default Context;
